import { cn } from '@mxa-argos/lib/utils';
import { useTranslation } from 'react-i18next';

export type BadgeVariant = 'positive' | 'negative' | 'pending' | 'info' | 'neutral';

type BadgeColor = {
  bg: string;
  text: string;
  darkBg: string;
  darkText: string;
};

const VARIANT_COLORS: Record<BadgeVariant, BadgeColor> = {
  positive: {
    bg: 'bg-green-100',
    text: 'text-green-800',
    darkBg: 'dark:bg-green-900',
    darkText: 'dark:text-green-200',
  },
  negative: {
    bg: 'bg-red-100',
    text: 'text-red-800',
    darkBg: 'dark:bg-red-900',
    darkText: 'dark:text-red-200',
  },
  pending: {
    bg: 'bg-yellow-100',
    text: 'text-yellow-800',
    darkBg: 'dark:bg-yellow-900',
    darkText: 'dark:text-yellow-200',
  },
  info: {
    bg: 'bg-blue-100',
    text: 'text-blue-800',
    darkBg: 'dark:bg-blue-900',
    darkText: 'dark:text-blue-200',
  },
  neutral: {
    bg: 'bg-white border border-border',
    text: 'text-gray-800',
    darkBg: 'dark:bg-white',
    darkText: 'dark:text-gray-800',
  },
};

const POSITIVE_VALUES = new Set([
  'ACTIVE',
  'ENROLLED',
  'COMPLETED',
  'DELIVERED',
  'ENABLED',
  'CONFIRMED',
  'OK',
  'SUCCESS',
  'APPROVED',
  'PAID',
  'SHIPPED',
  'RELEASED',
]);

const NEGATIVE_VALUES = new Set([
  'CANCELLED',
  'FAILED',
  'ERROR',
  'DISABLED',
  'REJECTED',
  'DECEASED',
  'LOST',
  'EXPIRED',
  'BLOCKED',
  'INACTIVE',
  'FRAUD',
  'FRAUD_PROFILE',
]);

const PENDING_VALUES = new Set([
  'PENDING',
  'PROCESSING',
  'SHIPPING',
  'IN_PROGRESS',
  'IN_PREPARATION',
  'REQUESTED',
  'WAITING',
  'PAUSED',
  'DRAFT',
  'CANCELLATION_REQUESTED',
  'NO_SIM',
  'PENDING_ACTIVATION',
  'PENDING_CANCELLATION',
  'SUSPICIOUS_UNPAID',
  'CONTACT_DATA_IN_USE',
  'SUSPICION',
]);

const INFO_VALUES = new Set(['PACKAGED', 'REQUIRES_MANUAL_ACTION']);

const NEUTRAL_VALUES = new Set(['AD_HOC', 'DISCARDED']);

function getVariantForValue(value: string): BadgeVariant | undefined {
  if (POSITIVE_VALUES.has(value)) return 'positive';
  if (NEGATIVE_VALUES.has(value)) return 'negative';
  if (PENDING_VALUES.has(value)) return 'pending';
  if (INFO_VALUES.has(value)) return 'info';
  if (NEUTRAL_VALUES.has(value)) return 'neutral';
  return undefined;
}

const BADGE_COLORS: BadgeColor[] = [
  {
    bg: 'bg-blue-100',
    text: 'text-blue-800',
    darkBg: 'dark:bg-blue-900',
    darkText: 'dark:text-blue-200',
  },
  {
    bg: 'bg-green-100',
    text: 'text-green-800',
    darkBg: 'dark:bg-green-900',
    darkText: 'dark:text-green-200',
  },
  {
    bg: 'bg-yellow-100',
    text: 'text-yellow-800',
    darkBg: 'dark:bg-yellow-900',
    darkText: 'dark:text-yellow-200',
  },
  {
    bg: 'bg-red-100',
    text: 'text-red-800',
    darkBg: 'dark:bg-red-900',
    darkText: 'dark:text-red-200',
  },
  {
    bg: 'bg-purple-100',
    text: 'text-purple-800',
    darkBg: 'dark:bg-purple-900',
    darkText: 'dark:text-purple-200',
  },
  {
    bg: 'bg-pink-100',
    text: 'text-pink-800',
    darkBg: 'dark:bg-pink-900',
    darkText: 'dark:text-pink-200',
  },
  {
    bg: 'bg-indigo-100',
    text: 'text-indigo-800',
    darkBg: 'dark:bg-indigo-900',
    darkText: 'dark:text-indigo-200',
  },
  {
    bg: 'bg-orange-100',
    text: 'text-orange-800',
    darkBg: 'dark:bg-orange-900',
    darkText: 'dark:text-orange-200',
  },
  {
    bg: 'bg-teal-100',
    text: 'text-teal-800',
    darkBg: 'dark:bg-teal-900',
    darkText: 'dark:text-teal-200',
  },
  {
    bg: 'bg-cyan-100',
    text: 'text-cyan-800',
    darkBg: 'dark:bg-cyan-900',
    darkText: 'dark:text-cyan-200',
  },
] as const;

// Green, yellow and red carry status semantics (positive/pending/negative), so the
// hash fallback only distributes colors without semantic weight.
const NEUTRAL_FALLBACK_COLOR_INDICES = [0, 4, 5, 6, 7, 8, 9] as const;

// Values that would collide with a sibling value of the same enum under the hash
// fallback get a stable explicit color.
const VALUE_COLOR_INDICES: Record<string, number> = {
  NEW_NUMBER: 8,
  PORTABILITY: 5,
};

function hashString(str: string): number {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 33) ^ str.charCodeAt(i);
  }
  return Math.abs(hash);
}

function getColorForValue(value: string): BadgeColor {
  const overrideIndex = VALUE_COLOR_INDICES[value];
  if (overrideIndex !== undefined) {
    return BADGE_COLORS[overrideIndex];
  }
  const neutralIndex =
    NEUTRAL_FALLBACK_COLOR_INDICES[hashString(value) % NEUTRAL_FALLBACK_COLOR_INDICES.length];
  return BADGE_COLORS[neutralIndex];
}

interface EnumBadgeProps {
  enumName?: string;
  value: string | null | undefined;
  size?: 'sm' | 'default';
  variant?: BadgeVariant;
  count?: number;
}

export const EnumBadge: React.FC<EnumBadgeProps> = ({
  enumName,
  value,
  size = 'default',
  variant,
  count,
}) => {
  const { t } = useTranslation();

  if (value == null) {
    return <span className="text-muted-foreground">-</span>;
  }

  const detectedVariant = variant ?? getVariantForValue(value);
  const color = detectedVariant ? VARIANT_COLORS[detectedVariant] : getColorForValue(value);
  const label = enumName ? t(`enums.${enumName}.${value}`, { defaultValue: value }) : value;

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full font-medium',
        size === 'sm' ? 'px-2 py-0.5 text-xs' : 'px-2.5 py-0.5 text-sm',
        color.bg,
        color.text,
        color.darkBg,
        color.darkText,
      )}
    >
      {label}
      {count !== undefined && <span className="tabular-nums">{count}</span>}
    </span>
  );
};
